import { cn, InputGroup, InputGroupAddon, InputGroupInput } from "@pdfking/components"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import { Items } from "@pdfking/tfunc"
import { XIcon } from "lucide-react"

import { InputMixin } from "../mixins/InputMixin"
import { TRAILING_ICON_PADDING } from "./classes"

export const DjInput = CreateRenderFunction({
  mixins: [InputMixin] as const,
  props: {
    // Whether the X shows — defaults to "the field has a value". Callers
    // whose value isn't in the input (DjDatalist keeps it in the
    // placeholder) say so explicitly.
    clearable: undefined as boolean | undefined,
    error: undefined as unknown,
    // Distinct from `oncommit`: the X button means "clear the value", always
    // — falls back to `oncommit("")` for callers that don't need the
    // difference. `oncommit` alone can't tell "explicitly cleared" apart from
    // "blurred with nothing typed", which some callers treat differently
    // (e.g. DjDatalist reverts the latter instead of clearing).
    onclear: undefined as (() => void) | undefined,
    onclick: undefined as ((e: MouseEvent) => void) | undefined,
    oncommit: undefined as ((v: string) => void) | undefined,
    input: "",
    placeholder: undefined as string | undefined,
    suggestions: undefined as Record<string, string | undefined> | undefined,
  },
  render() {
    const commit = (e: Event) => this.oncommit?.((e.target as HTMLInputElement).value)
    const value = String(this.input ?? "")
    return (
      <>
        <InputGroup className="h-8">
          <InputGroupInput
            aria-invalid={this.error ? true : undefined}
            autocomplete="off"
            // `px-2` is DjSelect's own inset (`INPUT_CLASS`), not
            // InputGroupInput's `px-3` — the two sit side by side in every form
            // row, so their text has to start on the same line, and 0.5rem is
            // what Bootstrap gives `.form-control-sm` too. It has to be stated
            // unconditionally: InputGroup only insets the input via its
            // `has-[>[data-align=inline-*]]` variants, which don't match while
            // the field is empty and the clear addon isn't rendered.
            //
            // No right-hand gutter to add: the addon is an in-flow flex sibling
            // that occupies its own width, so nothing overlaps the text. (That
            // is the difference from DjSelect, whose caret is absolutely
            // positioned and does need `pr-6` to reserve room.)
            className="px-2 text-sm"
            disabled={this.disabled}
            id={this.dom_id}
            list={`dl-${this.dom_id}`}
            onBlur={commit}
            onClick={this.onclick}
            // Picking a datalist suggestion is a commit, not typing: Chrome and
            // Safari fire a plain `Event`, Firefox an `insertReplacementText`
            // InputEvent — real keystrokes are InputEvents of other types.
            onInput={(e: Event) => {
              if (!(e instanceof InputEvent) || e.inputType === "insertReplacementText") commit(e)
            }}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                e.stopPropagation()
                commit(e)
              }
            }}
            placeholder={this.placeholder}
            type="text"
            value={value}
          />
          {/* An inline-end addon, the same slot the native select caret sits
              in — `TRAILING_ICON_PADDING` is the shared inset (see
              fields/classes.ts) so the clear button and the caret line up
              across the form.

              The <span> is load-bearing. `InputGroupAddon` pulls a *direct*
              button child outward by -0.45rem to cancel the padding an
              `InputGroupButton` carries; ours is a bare `p-0` icon with no
              padding to cancel, so that shift is simply wrong here and drags
              the X off the caret's line. Breaking the `has-[>button]` match is
              what stops it — an `!mr-0` override only works while nothing else
              on the element is `!important`, which stopped being true the
              moment the embedded skin generated its utilities with Tailwind's
              `important` flag (see @pdfking/components/theme-bootstrap). */}
          {(this.clearable ?? !!value) && !this.disabled && (
            <InputGroupAddon align="inline-end" className={TRAILING_ICON_PADDING}>
              <span className="flex">
                <button
                  className={cn("p-0 text-muted-foreground hover:text-foreground")}
                  onClick={this.onclear ?? (() => this.oncommit?.(""))}
                  tabIndex={-1}
                  type="button"
                >
                  <XIcon className="size-4" />
                </button>
              </span>
            </InputGroupAddon>
          )}
        </InputGroup>
        {this.suggestions && (
          <datalist id={`dl-${this.dom_id}`}>
            {Items(this.suggestions).map(([key, label]) => (
              <option key={key} label={label} value={key} />
            ))}
          </datalist>
        )}
      </>
    )
  },
})
